import React, {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ArrowRight} from '../../icons';
import {useAlertRulesAccess, useUncontrolledSpendAccess} from '../../../context/UserModulesContext';
import {UncontrolledSpendApi} from '../../../api/uncontrolledSpendApi';
import {isOk} from '../../../utils/apiResponse';
import {ScreenTracker} from '../../../utils/ScreenTracker';
import {formatCompactMoney} from '../../client/panel_v11/panelFormatters';
import {uncontrolledPath} from '../alertsCenterPaths';

const RAW = {interpolation: {escapeValue: false}};

// El renglón del Tablero, de la ficha y del Listado va atado al Centro de Alertas (fede, 26/10): se ve en las
// agencias que tienen el Centro y les llega a las demás el día que se les prende, sin otro deploy. Se lanza y
// se comunica junto con el Centro. La solapa, el mail y la tarjeta de «Ahora» (adentro de Alertas) no dependen
// de esto. Hasta el 26/10 lo veía sólo la Agencia Demo (`b62234f0`, fede 19/10).
const ALWAYS_VISIBLE_VARIANT = '-now';

/** ¿Se dibuja? «Ahora» siempre; el Tablero, la ficha y el Listado, sólo en una agencia con el Centro. */
export function visibleWithAlertsCenter(className, alertsCenterEnabled) {
    const variants = String(className || '').split(/\s+/);
    if (variants.includes(ALWAYS_VISIBLE_VARIANT)) return true;
    return alertsCenterEnabled === true;
}

/** El nombre del evento de clic de la tarjeta: `-board` → `uncontrolled_card_open_board`. */
export function cardOpenEvent(className) {
    const variant = String(className || '').split(/\s+/).find((name) => name.startsWith('-'));
    return variant ? `uncontrolled_card_open_${variant.slice(1)}` : 'uncontrolled_card_open';
}

/**
 * «≈ ARS 513 mil sin control esta semana · En 4 cuentas · Ver →»: el renglón gris que lleva a la solapa
 * «Inversión sin control» (UD-5, 17/10). Es el ÚNICO lugar fuera de la solapa donde se habla de esa plata: va
 * en el Tablero (entre el encabezado y la tabla), en la card de líneas de la ficha (con `clientId`: los números
 * de ESE cliente y el link a la solapa acotada), en el Listado y arriba de «Ahora».
 *
 * - **Se protege sola** (`useUncontrolledSpendAccess`): sin el permiso, o en el clásico, no pide ni dibuja; y
 *   fuera de «Ahora», tampoco sin el Centro de Alertas (`useAlertRulesAccess`). Así se monta sin condiciones,
 *   también desde un componente de clase como el Tablero.
 * - Habla **sólo de plata**: si el pedido falla o el total es 0, no se dibuja y la pantalla sigue igual,
 *   aunque haya cuentas no medidas.
 * - Al pasar de la ficha del cliente A a la de B (la pantalla no se remonta), el número de A no queda: el
 *   efecto depende de `clientId`, vacía la tarjeta y cada pedido lleva un número; sólo escribe el último.
 * - El clic se cuenta por variante (`uncontrolled_card_open_board`, `_panel`, `_list`, `_now`).
 */
export function UncontrolledSummaryCard({clientId = null, className = ''}) {
    const {t} = useTranslation();
    const {enabled: allowed} = useUncontrolledSpendAccess();
    const {enabled: alertsCenter} = useAlertRulesAccess();
    const enabled = allowed && visibleWithAlertsCenter(className, alertsCenter);
    const [summary, setSummary] = useState(null);
    const requestSeq = useRef(0);

    useEffect(() => {
        const seq = ++requestSeq.current;
        setSummary(null);
        if (!enabled) return undefined;
        UncontrolledSpendApi.summary({clientId}).then((response) => {
            if (seq !== requestSeq.current) return;
            if (isOk(response) && response.result) setSummary(response.result);
        });
        // Desmontar (o cambiar de cliente) deja vieja la respuesta en vuelo.
        return () => { requestSeq.current += 1; };
    }, [enabled, clientId]);

    if (!enabled || !summary || !summary.totals) return null;
    const {totals, currency, items_count: count} = summary;
    if (!(totals.uncontrolled > 0) && !(totals.limit_only > 0)) return null;

    const main = totals.uncontrolled > 0
        ? t('alerts_center.uncontrolled.card.uncontrolled', {amount: formatCompactMoney(currency, totals.uncontrolled), ...RAW})
        : t('alerts_center.uncontrolled.card.limit_only', {amount: formatCompactMoney(currency, totals.limit_only), ...RAW});

    return (
        <Link to={uncontrolledPath({clientId})} className={`alc-unc-card${className ? ` ${className}` : ''}`}
              data-testid="alc-unc-card" onClick={() => ScreenTracker.recordEvent(cardOpenEvent(className))}>
            <span className="alc-unc-card__dot" aria-hidden="true"/>
            <span className="alc-unc-card__text">
                <strong>{main}</strong>
                <span className="alc-unc-card__sub">{t('alerts_center.uncontrolled.card.accounts', {count})}</span>
            </span>
            <span className="alc-unc-card__cta">
                {t('alerts_center.uncontrolled.card.see')}
                <ArrowRight size={13} weight="bold"/>
            </span>
        </Link>
    );
}

export default UncontrolledSummaryCard;
